/* @flow */
import cx from 'classnames';
import React from 'react';
import ReactDOM from 'react-dom';

import Button from 'components/Button.jsx';
import Avatar from 'components/Avatar.jsx';
import FlexibleBlock from 'components/FlexibleBlock.jsx';
import Glyph from 'components/Glyph.jsx';
import LeadActions from 'actions/LeadActions';
import Link from 'components/Link.jsx';
import NameAndSignatureQuery from 'components/NameAndSignatureQuery.jsx';
import PotentialChatMember from 'entities/PotentialChatMember';
import StatusLabel from 'components/StatusLabel.jsx';
import Text from 'components/Text.jsx';
import TextEntities from 'lib/TextEntities';
import TextWithEntities from 'components/TextWithEntities.jsx';
import ToastActions from 'actions/ToastActions';
import EventTracking from 'lib/EventTracking';

import './styles/ChatListSearchItem.scss';

const screenName = 'invite_teacher_leads_chat_search_item';

type Props = {
  member: PotentialChatMember,
  query: string,
  onSelect: (member: PotentialChatMember) => void,
  highlight: boolean,
  selected: boolean,
  added?: ?boolean,
};

class ChatListSearchItem extends React.Component {
  props: Props;

  static defaultProps = {
    highlight: false,
    selected: false,
  }

  componentDidMount() {
    const { member } = this.props;
    if (member.isTeacherLead()) {
      if (member.isInvited()) {
        EventTracking.view(screenName, {
          state: 'invited',
        });
      } else if (member.isInvitable()) {
        EventTracking.view(screenName, {
          state: 'invitable',
        });
      }
    }
  }

  // Avoid redrawing like crazy when the highlight
  // props is changed when hovered on element.
  shouldComponentUpdate(nextProps: Props) {
    return (
      nextProps.highlight !== this.props.highlight ||
      nextProps.query !== this.props.query ||
      nextProps.onSelect !== this.props.onSelect ||
      nextProps.selected !== this.props.selected ||
      nextProps.added !== this.props.added || !!nextProps.member.changed.state
    );
  }

  click = () => {
    const link = ReactDOM.findDOMNode(this.refs.link);
    link.click();
  }

  handleClick = (event: any) => {
    event.preventDefault();
    this.props.onSelect(this.props.member);
  }

  inviteMember = () => {
    const onSuccess = () => {
      ToastActions.showSuccessToast(
        `Thank you! ${this.props.member.getName()} has been sent an invitation.`
      );
    };

    LeadActions
      .sendInvitations([this.props.member.id], 'chat')
      .then(onSuccess, ToastActions.showDefaultErrorToast);
  }

  render() {
    const { member } = this.props;
    const fullName = member.getRole() === 'teacher' ?
      member.getUser().getFullName() :
      '';

    let subText = member.getConnectionDescription();
    let privateNote = member.getNote();

    if (this.props.query !== '') {
      const parser = TextEntities.createTextMatchingParser(this.props.query);
      subText = <TextWithEntities text={subText} entities={parser(subText)} />;

      if (privateNote) {
        const privateNoteMatches = parser(privateNote);
        privateNote = <TextWithEntities text={privateNote} entities={privateNoteMatches} />;
      }
    }

    if (privateNote) {
      privateNote = (
        <Text className="ChatListSearchItem-privateNote" truncate>
          {privateNote}
        </Text>
      );
    }

    const user = member.getUser();

    let item = (
      <div>
        <FlexibleBlock flex="right">
          <Avatar
            className="ChatListSearchItem-glyph"
            text={user.getInitials()}
            colorHex={user.getColor() || 'ADADAD'}
            size="medium"
          />
          <div className="ChatListSearchItem-bodyText">
            <Text className="ChatListSearchItem-name" truncate>
              <NameAndSignatureQuery
                displayName={member.getName()}
                fullName={fullName}
                query={this.props.query}
              />
            </Text>
            {privateNote}
            <Text className="ChatListSearchItem-subText" truncate>{subText}</Text>
            <hr className="ChatListSearchItem-divider" />
          </div>
        </FlexibleBlock>
      </div>
    );

    let state;
    if (this.props.added) {
      state = 'Added';
    } else if (member.isPending()) {
      state = 'Pending';
    } else if (member.isUnder13()) {
      state = 'Under 13';
    }

    if (member.isTeacherLead()) {
      if (member.isInvited()) {
        item = (
          <FlexibleBlock flex="left">
            {item}
            <StatusLabel className="ChatListSearchItem-status">Invited</StatusLabel>
          </FlexibleBlock>);
      } else if (member.isInvitable()) {
        item = (
          <FlexibleBlock flex="left">
            {item}
            <Button
              className="ChatListSearchItem-inviteButton"
              size="xsmall"
              level="secondary"
              onClick={this.inviteMember}
              eventProperties={{
                teacher_lead_id: member.id,
              }}
              eventElementName="ChatListSearchItem-inviteButton">
              Invite
            </Button>
          </FlexibleBlock>);
      } else {
        return null; // if a teacher lead is neither invited nor invitable, don't display them
      }
    }

    if (this.props.selected) {
      item = (
        <FlexibleBlock flex="left">
          {item}
          <Glyph
            className="ChatListSearchItem-selectedGlyph"
            name="stamp-check"
            size="medium"
            color="brand"
          />
        </FlexibleBlock>
      );
    } else if (state) {
      item = (
        <FlexibleBlock flex="left">
          {item}
          <StatusLabel className="ChatListSearchItem-status">{state}</StatusLabel>
        </FlexibleBlock>
      );
    }

    return (
      <li
        className={cx({
          ChatListSearchItem: true,
          'ChatListSearchItem-highlight': this.props.highlight,
          'ChatListSearchItem-under13': member.isUnder13(),
          'ChatListSearchItem-unavailable': member.isUnavailable(),
          'ChatListSearchItem-selected': this.props.selected,
          'ChatListSearchItem-added': this.props.added,
          'ChatListSearchItem-invite': member.isInvitable() || member.isInvited(),
        })}>
        <Link
          className="ChatListSearchItem-link"
          ref="link"
          color="pitch"
          onClick={this.handleClick}
          eventElementName="ChatListSearchItem-link">
          <div
            className="ChatListSearchItem-linkContent">
            {item}
          </div>
        </Link>
      </li>
    );
  }
}

export default ChatListSearchItem;



// WEBPACK FOOTER //
// ./src/chat/components/ChatListSearchItem.jsx